import { useCallback, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CreditCard,
  Landmark,
  ShieldAlert,
} from 'lucide-react';
import { loadConnectAndInitialize, type LoadError } from '@stripe/connect-js';
import {
  ConnectAccountManagement,
  ConnectBalances,
  ConnectComponentsProvider,
  ConnectDisputesList,
  ConnectPaymentDetails,
  ConnectPaymentDisputes,
  ConnectPayments,
  ConnectPayouts,
} from '@stripe/react-connect-js';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { usePaymentSettings } from '@/hooks/settings/usePaymentSettings';
import { useStripeAccountSession } from '@/hooks/settings/useStripeAccountSession';
import { apiPost, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import type { StripeAccountSessionResponse } from '@/types/settings';

type StripeManagementTab = 'payments' | 'disputes' | 'payouts' | 'account';

const TAB_LABEL: Record<StripeManagementTab, string> = {
  payments: 'Payments',
  disputes: 'Disputes',
  payouts: 'Payouts',
  account: 'Account',
};
const FALLBACK_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

export const Route = createFileRoute('/_dashboard/dashboard/stripe')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: normalizeTab(search.tab),
    payment: search.payment ? String(search.payment) : null,
  }),
  component: StripeManagementPage,
});

function normalizeTab(value: unknown): StripeManagementTab {
  return value === 'disputes' || value === 'payouts' || value === 'account' ? value : 'payments';
}

function StripeManagementPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { data: settings, isLoading: isSettingsLoading } = usePaymentSettings();
  const sessionQuery = useStripeAccountSession(settings?.stripe.accountId != null);
  const [componentError, setComponentError] = useState<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    const response = await apiPost<StripeAccountSessionResponse>(
      operationsClient,
      settingsEndpoints.stripeAccountSession(),
    );
    return response.clientSecret;
  }, []);

  const connectInstance = useMemo(() => {
    const publishableKey = sessionQuery.data?.publishableKey || FALLBACK_PUBLISHABLE_KEY;
    if (!publishableKey) {
      return null;
    }

    return loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret,
      appearance: {
        overlays: 'drawer',
        variables: {
          colorPrimary: '#111827',
          spacingUnit: '8px',
          fontFamily: 'Inter, sans-serif',
          headingXlFontWeight: '600',
          headingLgFontWeight: '600',
          headingMdFontWeight: '600',
          headingSmFontWeight: '600',
          headingXsFontWeight: '600',
        },
      },
    });
  }, [fetchClientSecret, sessionQuery.data?.publishableKey]);

  const selectedPayment = search.payment?.trim() || null;

  const handleComponentError = useCallback(({ error }: LoadError) => {
    setComponentError(error.message ?? 'Stripe component failed to load.');
  }, []);

  if (isSettingsLoading || (settings?.stripe.accountId && sessionQuery.isLoading)) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full max-w-xl" />
        <Skeleton className="h-[420px] w-full" />
      </div>
    );
  }

  if (!settings?.stripe.accountId) {
    return (
      <div className="max-w-2xl rounded-lg border bg-card p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-100 p-2 text-amber-700">
            <AlertTriangle size={16} />
          </div>
          <div className="space-y-3">
            <div>
              <h1 className="text-lg font-semibold">Stripe is not connected</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Connect Stripe first before opening embedded payment and dispute tools.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() =>
                void navigate({
                  to: '/dashboard/settings/billing/payments',
                })
              }
            >
              Open Payment Settings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!connectInstance || sessionQuery.isError) {
    return (
      <div className="max-w-2xl rounded-lg border bg-card p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-red-100 p-2 text-red-700">
            <AlertTriangle size={16} />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Unable to open Stripe tools</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {sessionQuery.error?.message ??
                (!FALLBACK_PUBLISHABLE_KEY
                  ? 'No Stripe publishable key is available for the embedded client.'
                  : 'Stripe account session could not be created for this tenant.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 w-fit"
            onClick={() =>
              void navigate({
                to: '/dashboard/payments',
              })
            }
          >
            <ArrowLeft size={14} className="mr-1.5" />
            Back to Payments
          </Button>
          <h1 className="text-2xl font-semibold">Stripe Management</h1>
          <p className="text-sm text-muted-foreground">
            Review payments, handle disputes, manage payouts, and update Stripe account details{' '}
            without leaving Notifi.
          </p>
        </div>

        <div className="rounded-lg border bg-card px-4 py-3 text-sm">
          <p className="font-medium">{settings.stripe.displayName ?? 'Stripe account'}</p>
          <p className="mt-1 text-muted-foreground">
            Status:{' '}
            <span className="capitalize">{settings.stripe.status.replaceAll('_', ' ')}</span>
          </p>
        </div>
      </div>

      <Tabs
        value={search.tab}
        onValueChange={(value) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              tab: normalizeTab(value),
            }),
            replace: true,
          })
        }
      >
        <TabsList className="h-9">
          <TabsTrigger value="payments" className="gap-1.5 px-3 text-xs">
            <CreditCard size={13} />
            {TAB_LABEL.payments}
          </TabsTrigger>
          <TabsTrigger value="disputes" className="gap-1.5 px-3 text-xs">
            <ShieldAlert size={13} />
            {TAB_LABEL.disputes}
          </TabsTrigger>
          <TabsTrigger value="payouts" className="gap-1.5 px-3 text-xs">
            <Landmark size={13} />
            {TAB_LABEL.payouts}
          </TabsTrigger>
          <TabsTrigger value="account" className="gap-1.5 px-3 text-xs">
            <Building2 size={13} />
            {TAB_LABEL.account}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {componentError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {componentError}
        </div>
      )}

      {search.tab === 'disputes' && selectedPayment && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
          Use the Stripe dispute tools below to accept the dispute or submit evidence. Notifi
          surfaces the dispute state and audit trail, but Stripe owns the actual dispute workflow.
        </div>
      )}

      <ConnectComponentsProvider connectInstance={connectInstance}>
        {search.tab === 'payments' && (
          <div className="space-y-4">
            {selectedPayment && (
              <div className="space-y-4">
                <div className="rounded-lg border bg-card px-4 py-3">
                  <p className="text-sm font-medium">Focused Stripe payment</p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">{selectedPayment}</p>
                </div>

                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.9fr)]">
                  <div className="min-h-[420px] rounded-lg border bg-card p-3">
                    <ConnectPaymentDetails
                      payment={selectedPayment}
                      onClose={() =>
                        void navigate({
                          search: (prev) => ({
                            ...prev,
                            payment: null,
                          }),
                          replace: true,
                        })
                      }
                      onLoadError={handleComponentError}
                    />
                  </div>
                  <div className="min-h-[420px] rounded-lg border bg-card p-3">
                    <ConnectPaymentDisputes
                      payment={selectedPayment}
                      onLoadError={handleComponentError}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="min-h-[640px] rounded-lg border bg-card p-3">
              <ConnectPayments onLoadError={handleComponentError} />
            </div>
          </div>
        )}

        {search.tab === 'disputes' && (
          <div className="space-y-4">
            {selectedPayment && (
              <div className="min-h-[420px] rounded-lg border bg-card p-3">
                <ConnectPaymentDisputes
                  payment={selectedPayment}
                  onLoadError={handleComponentError}
                />
              </div>
            )}
            <div className="min-h-[640px] rounded-lg border bg-card p-3">
              <ConnectDisputesList onLoadError={handleComponentError} />
            </div>
          </div>
        )}

        {search.tab === 'payouts' && (
          <div className="space-y-4">
            <div className="min-h-[220px] rounded-lg border bg-card p-3">
              <ConnectBalances onLoadError={handleComponentError} />
            </div>
            <div className="min-h-[640px] rounded-lg border bg-card p-3">
              <ConnectPayouts onLoadError={handleComponentError} />
            </div>
          </div>
        )}

        {search.tab === 'account' && (
          <div className="min-h-[720px] rounded-lg border bg-card p-3">
            <ConnectAccountManagement onLoadError={handleComponentError} />
          </div>
        )}
      </ConnectComponentsProvider>
    </div>
  );
}
